import { format } from "date-fns";
import React, { useState } from "react";
import { Box } from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridPaginationModel,
} from "@mui/x-data-grid";
import { EmailRecipient } from "../../core/api/email/BatchEmailModel";
import PageRequest from "../../core/api/PageRequest";
import PageResponse from "../../core/api/PageResponse";
import Check from "@mui/icons-material/Check";
import { translations } from "../../translations/common";
import { tableContainerStyles, tableHeaderStyles, dataGridStyles } from "../common/styles/TableStyles";

interface Props {
  fetchFunction: (page: PageRequest) => Promise<PageResponse<EmailRecipient>>;
}

const EmailRecipientsTable: React.FC<Props> = ({ fetchFunction }) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<(EmailRecipient & { id: string })[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 10,
    page: 0,
  });

  const columns: GridColDef[] = [
    {
      field: "clientId",
      headerName: translations.emailRecipientsTable.columns.clientId,
      flex: 1,
      renderCell: (params: GridRenderCellParams<EmailRecipient>) => (
        <Box>{params.row.clientId || '-'}</Box>
      )
    },
    {
      field: "email",
      headerName: translations.emailRecipientsTable.columns.email,
      flex: 1,
      renderCell: (params: GridRenderCellParams<EmailRecipient>) => (
        <Box>{params.row.email || '-'}</Box>
      )
    },
    {
      field: "createdAt",
      headerName: translations.emailRecipientsTable.columns.date,
      flex: 1,
      minWidth: 160,
      renderCell: (params: GridRenderCellParams<EmailRecipient>) => {
        const createdAt = params.row.createdAt;
        if (!createdAt) return <Box>-</Box>;

        try {
          let formattedDate = format(new Date(createdAt), "yyyy-MM-dd HH:mm:ss");
          return <Box>{formattedDate}</Box>;
        } catch (e) {
          console.error('Error formatting date:', e, createdAt);
          return <Box>{String(createdAt)}</Box>;
        }
      }
    },
    {
      field: "status",
      headerName: translations.emailRecipientsTable.columns.status,
      flex: 1,
      renderCell: (params: GridRenderCellParams<EmailRecipient>) => {
        const status = params.row.status;
        let translatedStatus = '';

        if (status && translations.emailRecipientsTable.statusOptions) {
          translatedStatus = translations.emailRecipientsTable.statusOptions[
            status as keyof typeof translations.emailRecipientsTable.statusOptions
          ] || status;
        }

        return <Box>{translatedStatus || status || '-'}</Box>;
      }
    },
    {
      field: "errorMessage",
      headerName: translations.emailRecipientsTable.columns.error,
      flex: 1,
      renderCell: (params: GridRenderCellParams<EmailRecipient>) => {
        const errorMessage = params.row.errorMessage;
        return <Box>{errorMessage || translations.emailRecipientsTable.noError || '-'}</Box>;
      }
    },
    {
      field: "opened",
      headerName: translations.emailRecipientsTable.columns.opened,
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<EmailRecipient>) => {
        return params.row.opened ? <Check /> : null;
      }
    }
  ];

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchFunction({
        page: paginationModel.page,
        size: paginationModel.pageSize,
      });

      const rowsWithId = result.content.map(item => {
        return {
          ...item,
          id: `${item.clientId}-${item.email}-${new Date().getTime()}` // Ensure unique ID
        };
      });

      setRows(rowsWithId);
      setTotalCount(result.page.totalElements);
    } catch (error) {
      console.error("Failed to fetch email recipients:", error);
    } finally {
      setLoading(false);
    }
  }, [fetchFunction, paginationModel.page, paginationModel.pageSize]);

  // Fetch data when dependencies change
  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Box sx={tableContainerStyles}>
      <Box sx={tableHeaderStyles}>
        {translations.emailRecipientsTable.title}
      </Box>
      <DataGrid
        rows={rows}
        columns={columns.map(col => ({ ...col, sortable: false }))}
        loading={loading}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10, 20]}
        rowCount={totalCount}
        rowHeight={60}
        paginationMode="server"
        disableRowSelectionOnClick
        disableColumnMenu
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        slotProps={{
          pagination: {
            showFirstButton: true,
            showLastButton: true,
          },
        }}
        slots={{
          noRowsOverlay: () => (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              {translations.emailRecipientsTable.noData}
            </Box>
          ),
        }}
        sx={{
          ...dataGridStyles,
          height: "70vh"
        }}
      />
    </Box>
  );
};

export default EmailRecipientsTable;
